import { Component, h } from 'preact'
import { isInputEl } from '../../libs/preact-entry'
import Icon from '../assets'
import algoliasearch from 'algoliasearch/lite'
import AutoSuggestTags from './AutoSuggestTags'
import getWithInMemoryCache from '../../utils/getWithInMemoryCache'
import { SEARCH_TOKEN_URL } from '../../libs/config'
import globals from '../../utils/globals'
import { common } from '../common'
// @ts-ignore
import Downshift from 'downshift/preact'
import Phrase from '../../libs/translationsUtils/phrase'
import translations from '../../libs/translationsUtils/translations'

interface Tag {
  id?: number,
  name: string
}

type TagList = Array<Tag>

interface Props {
  isEdit: boolean,
  inputName: string,
  inputId?: string,
  selectedPublicTags?: string,
  canEndUserAddPublicTags: boolean,
  onChange?: (i: Array<string>) => void,
  iconSize?: number
}
interface FacetHit {
  value: string,
  highlighted: string,
  count: number,
}
interface State {
  publicTagList: FacetHit[],
  selectedPublicTags: TagList,
  selectedTagsSet: Set<string>,
  searchText: string,
  index?: algoliasearch.Index
}

export default class PublicTagSelect extends Component<Props, State> {
  tagInput: HTMLInputElement | undefined
  dropDown: HTMLDivElement | undefined;

  constructor (props: Props) {
    super(props)

    const selectedTagNames: TagList = props.selectedPublicTags?.length ? props.selectedPublicTags.split(',').map(t => ({ name: t })) : []
    const selectedTagsSet = new Set(selectedTagNames.map(t => t.name.toLowerCase()))

    this.state = {
      publicTagList: [],
      selectedPublicTags: selectedTagNames,
      selectedTagsSet: selectedTagsSet,
      searchText: ''
    }

    this.selectPublicTag = this.selectPublicTag.bind(this)
    this.removePublicTag = this.removePublicTag.bind(this)
    this.updateSearchText = this.updateSearchText.bind(this)
    this.refreshSuggestions = this.refreshSuggestions.bind(this)
    this.isTagSelected = this.isTagSelected.bind(this)
    this.handleFocusChange = this.handleFocusChange.bind(this)
    this.handleOnBlur = this.handleOnBlur.bind(this)
    this.onCancelEditPublicTags = this.onCancelEditPublicTags.bind(this)
  }

  async componentDidMount () {
    const data: {client_id: string, token: string} = await getWithInMemoryCache(SEARCH_TOKEN_URL, {})
    const client = algoliasearch(data.client_id, data.token)
    this.setState({ index: client.initIndex(globals('communityId')) })
    this.state.index.searchForFacetValues({ facetName: 'tags', facetQuery: '', facetingAfterDistinct: true }).then(({ facetHits }: { facetHits: FacetHit[] }) => {
      this.setState({ publicTagList: facetHits })
    })
  }

  updateSearchText (e: KeyboardEvent, reset: Function) {
    if (e.code === 'Escape') {
      this.tagInput && this.tagInput.blur()
      this.setState({ searchText: '' })
      reset()
      return
    }

    const target = e?.target
    if (!isInputEl(target)) {
      return
    }

    const value = target.value
    if (e.code === 'Backspace' && !this.state.searchText.length) {
      this.removeLastTag()
    }

    this.refreshSuggestions(value)
  }

  refreshSuggestions (searchText: string) {
    this.state.index.searchForFacetValues({ facetName: 'tags', facetQuery: searchText, facetingAfterDistinct: true }).then(({ facetHits }: { facetHits: FacetHit[] }) => {
      this.setState({ publicTagList: facetHits, searchText: searchText })
      this.tagInput && this.tagInput.focus()
    })
  }

  isTagSelected (tag: string) {
    return this.state.selectedTagsSet.has(tag.toLowerCase())
  }

  selectPublicTag (tag: Tag, remainFocusOnInput: boolean = true) {
    const { selectedPublicTags, selectedTagsSet } = this.state
    if (this.isTagSelected(tag.name)) {
      return
    }

    selectedTagsSet.add(tag.name.toLowerCase())
    selectedPublicTags.push(tag)

    this.setState({
      selectedPublicTags: selectedPublicTags,
      selectedTagsSet: selectedTagsSet,
      searchText: ''
    })

    if (this.props.onChange) {
      const tagsList = selectedPublicTags.map(tag => { return tag.name })
      this.props.onChange(tagsList)
    }

    if (remainFocusOnInput) {
      this.refreshSuggestions('')
    }
  }

  removeLastTag () {
    const { selectedPublicTags } = this.state

    if (!selectedPublicTags.length) {
      return
    }

    const lastSelected = selectedPublicTags[selectedPublicTags.length - 1]
    this.removePublicTag(lastSelected)
    this.refreshSuggestions('')
  }

  removePublicTag (tag: Tag) {
    const { selectedPublicTags, selectedTagsSet } = this.state

    if (!this.isTagSelected(tag.name)) {
      return
    }

    selectedTagsSet.delete(tag.name.toLowerCase())
    const index = selectedPublicTags.indexOf(tag)
    selectedPublicTags.splice(index, 1)

    if (this.props.onChange) {
      const tagsList = selectedPublicTags.map(tag => { return tag.name })
      this.props.onChange(tagsList)
    }

    this.setState({ selectedPublicTags: selectedPublicTags, selectedTagsSet: selectedTagsSet })
  }

  handleFocusChange (openMenu: Function) {
    openMenu()
    this.refreshSuggestions('')
  }

  handleOnBlur (reset: Function, closeMenu: Function) {
    this.setState({ searchText: '' })
    closeMenu()
    reset()
  }

  onCancelEditPublicTags () {
    const selectedTagNames: TagList = this.props.selectedPublicTags?.length ? this.props.selectedPublicTags.split(',').map(t => ({ name: t })) : []
    const selectedTagsSet = new Set(selectedTagNames.map(t => t.name.toLowerCase()))
    this.setState({
      selectedPublicTags: selectedTagNames,
      selectedTagsSet: selectedTagsSet,
      searchText: ''
    })
  }

  render ({ isEdit, inputName, inputId, canEndUserAddPublicTags, iconSize }: Props, { publicTagList, selectedPublicTags, searchText, index }: State) {
    const tags = publicTagList.filter(tag => {
      return !this.isTagSelected(tag.value)
    })
    const closeIconSize = iconSize ?? 16
    return (<div><Downshift
      defaultHighlightedIndex={0}
      initialHighlightedIndex={0}
      onChange={(selection: {value: string}) => this.selectPublicTag({ name: selection.value })}
      itemToString={(tag:Tag) => (tag ? tag.name : '')}
    >{({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        isOpen,
        openMenu,
        closeMenu,
        inputValue,
        highlightedIndex,
        selectedItem,
        reset
      }: Downshift) => (
        <div className='public-tag-container qa-public-tag-container'>
          <div className={`public-tag-wrapper ${isEdit ? 'public-tag-edit-wrapper' : ''}`}>
            {isEdit
              ? (selectedPublicTags.length ? (
                selectedPublicTags.map(tag => (
                  <input type='hidden' name={inputName} value={tag.name} />
                ))) : null)
              : <input type='hidden' name={inputName} id={inputId} value={selectedPublicTags.map(t => t.name).join(',')} />
            }
            {
              selectedPublicTags.length ? (selectedPublicTags.map(selected => {
                return (
                  <div className='public-tag_selected-tag' onClick={() => this.removePublicTag(selected)}>
                    <div className='public-tag_selected-tag-name'>{selected.name}</div>
                    <div
                      className='public-tag_selected-remove-button'
                    >
                      <Icon icon='close' width={closeIconSize} height={closeIconSize} color={'currentColor'} />
                    </div>
                  </div>
                )
              })) : null
            }
            <div className='public-tag-input-wrapper'>
              <input {...getInputProps()}
                title=''
                ref={input => { this.tagInput = input }}
                class='form-control'
                placeholder={translations.translate('Forum', 'tags.add')}
                type='text'
                onFocus={() => this.handleFocusChange(openMenu)}
                onBlur={() => this.handleOnBlur(reset, closeMenu)}
                onKeyUp={e => this.updateSearchText(e, reset)}
              />
            </div>
            {
              isOpen && <div class='public-tag-list-container' ref={element => { this.dropDown = element }}>
                <DropDown tags={tags}
                  selectPublicTag={(tag: Tag) => {
                    this.selectPublicTag(tag)
                    reset()
                  }}
                  searchText={searchText}
                  canEndUserAddPublicTags={canEndUserAddPublicTags}
                  isTagSelected={this.isTagSelected}
                  getMenuProps={getMenuProps}
                  getItemProps={getItemProps}
                  highlightedIndex={highlightedIndex} />
              </div>
            }
          </div>
          { common.isModuleActive('publicTagAutoSuggestion') &&
            <AutoSuggestTags index={index} isTagSelected={this.isTagSelected} selectPublicTag={this.selectPublicTag} />
          }
        </div>
      )}
    </Downshift>
    { isEdit
      ? (<div className='public-tag-edit-buttons'><button className='btn btn--cancel js-collapsable--trigger btn--S qa-button-tags-cancel' data-toggle='tags' onClick={this.onCancelEditPublicTags}><Phrase p='Cancel' m='Forum' /></button>
        <button className='btn btn--S btn--cta save-public-tag qa-button-tags-save' type='submit'><Phrase p='Save' m='Forum' /></button></div>) : null }
    </div>)
  }
}

const DropDown = ({ tags, selectPublicTag, canEndUserAddPublicTags, isTagSelected, searchText, getMenuProps, getItemProps, highlightedIndex }: {
  tags: FacetHit[],
  searchText: string,
  selectPublicTag: (tag: Tag) => void,
  isTagSelected: (tag: string) => boolean,
  canEndUserAddPublicTags: boolean,
  getMenuProps: Function,
  getItemProps: Function,
  highlightedIndex: number,
}) => {
  let options = []
  let showCreatePublicTagOption = canEndUserAddPublicTags && !tags.some(tag => tag.value.toLowerCase() === searchText.toLowerCase())
  if (tags.length > 0) {
    options.push(...tags.map((item, index) => {
      let modifiedIndex = showCreatePublicTagOption ? index + 1 : index
      return <li
        className='public-tag-list-item'
        {...
        getItemProps({
          key: item.value,
          index: modifiedIndex,
          item,
          class: highlightedIndex === modifiedIndex ? 'highlight-selected-option public-tag-list-item' : 'public-tag-list-item'
        })
        }
      >
        <span dangerouslySetInnerHTML={{ __html: item.highlighted }} />
        <span className='usage-count'>{item.count}</span>
      </li >
    }))
  } else if (!canEndUserAddPublicTags || !isTagSelected(searchText)) {
    options.push(<li className='public-tag_not-found'><Phrase p='live.search.no.result.found.text' m='Forum' /> {searchText}</li>)
  }
  if (showCreatePublicTagOption) {
    options.unshift(<AddNewPublicTag
      searchText={searchText}
      selectPublicTag={selectPublicTag}
      highlightedIndex={highlightedIndex}
      getItemProps={getItemProps} isTagSelected={isTagSelected} />)
  }

  return <ul {...getMenuProps()}>
    { options }
  </ul>
}

const AddNewPublicTag = ({ searchText, selectPublicTag, isTagSelected, getItemProps, highlightedIndex }: {
  searchText: string,
  selectPublicTag: (tag: Tag) => void,
  isTagSelected: (tag: string) => boolean,
  getItemProps: Function,
  highlightedIndex: number
}) => {
  if (searchText.length === 0) {
    return null
  }

  if (isTagSelected(searchText)) {
    return <li className='public-tag-list-item existing-tag'><Phrase p='tags.already.added' m='Forum' /></li>
  }

  return <li className='public-tag-list-item new-tag'
    {...getItemProps({
      key: searchText,
      index: 0,
      item: { value: searchText },
      class: highlightedIndex === 0 ? 'highlight-selected-option public-tag-list-item new-tag' : 'public-tag-list-item new-tag'
    })}
    onMouseDown={() => selectPublicTag({ name: searchText })}>
    <Phrase p='tags.add.new' m='Forum' /> '{searchText}'
  </li>
}

export { Tag }
